import React, { ReactElement, useEffect, useMemo, useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import logo from '../../assets/images/logo.svg'
import { logger } from '../../utils'
import { Formik, Form, Field, FieldArray, FormikProps, FormikHelpers } from 'formik'
import * as Yup from 'yup'

import { Button, Input, Text } from '../atoms'
import { ModalStripe } from '../molecules'
import ContactLinks from '../molecules/ContactLinks/ContactLinks'
import { toast } from 'react-toastify'

// --- Types ---
interface StudentData {
  email: string
  firstName: string
  lastName: string
  submittedAt?: string
}

interface SessionDetails {
  quantity: number
  paymentStatus: string
  customerEmail?: string
  flow?: string
  studentsSubmitted: number
  existingStudents: StudentData[]
  remainingSlots: number
  studentCollectionStatus: 'pending' | 'partial' | 'complete'
  productName: string
}

interface CouponInfo {
  code: string
  discountPercentage: number
}

type SessionLookupStatus = 'idle' | 'loading' | 'success' | 'error' | 'payment_pending'

interface StudentFormValues {
  students: StudentData[]
}

// --- API Functions ---
const CHECKOUT_ENDPOINT = 'https://aote22c7zh.execute-api.us-east-2.amazonaws.com/default/stripeCheckout'
const WEBHOOK_ENDPOINT = 'https://buxj4tmda4.execute-api.us-east-2.amazonaws.com/default/stripeWebHook'

const fetchSessionDetails = async (sessionId: string, signal?: AbortSignal): Promise<{ success: boolean, data?: SessionDetails, error?: string, code?: string }> => {
  try {
    const res = await fetch(CHECKOUT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'GET_SESSION_DETAILS', sessionId }),
      signal
    })
    const data = await res.json()
    if (data.success === true) {
      return {
        success: true,
        data: {
          quantity: data.quantity,
          paymentStatus: data.paymentStatus,
          customerEmail: data.customerEmail,
          flow: data.flow,
          studentsSubmitted: data.studentsSubmitted ?? 0,
          existingStudents: data.existingStudents ?? [],
          remainingSlots: data.remainingSlots ?? data.quantity,
          studentCollectionStatus: data.studentCollectionStatus ?? 'pending',
          productName: data.productName ?? 'DopaGE Prevention Portal - 1 year'
        }
      }
    }
    return { success: false, error: data.error ?? 'Failed to fetch session details', code: data.code }
  } catch (err) {
    if ((err as Error).name === 'AbortError') {
      return { success: false, error: 'Request aborted' }
    }
    return { success: false, error: 'Network error' }
  }
}

const submitStudents = async (sessionId: string, studentData: StudentData[]): Promise<{ success: boolean, message?: string, error?: string, code?: string }> => {
  try {
    const res = await fetch(CHECKOUT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'UPDATE_SESSION_STUDENTS',
        sessionId,
        studentData
      })
    })
    return await res.json()
  } catch (err) {
    return { success: false, error: 'Network error submitting students' }
  }
}

// --- Validation Schema ---
const StudentSchema = Yup.object().shape({
  students: Yup.array().of(
    Yup.object().shape({
      email: Yup.string().email('Invalid email format').required('Email is required'),
      firstName: Yup.string().required('First name is required'),
      lastName: Yup.string().required('Last name is required')
    })
  ).min(1, 'At least one student is required')
})

// --- Student Collection Form Component ---
interface StudentFormProps {
  sessionId: string
  sessionDetails: SessionDetails
  onSubmitSuccess: () => void
}

const StudentCollectionForm = ({ sessionId, sessionDetails, onSubmitSuccess }: StudentFormProps): ReactElement => {
  const [submitError, setSubmitError] = useState<string | null>(null)

  const initialStudents: StudentData[] = Array.from(
    { length: sessionDetails.remainingSlots },
    () => ({ email: '', firstName: '', lastName: '' })
  )

  const handleSubmit = async (values: StudentFormValues, actions: FormikHelpers<StudentFormValues>): Promise<void> => {
    setSubmitError(null)
    actions.setSubmitting(true)

    try {
      const result = await submitStudents(sessionId, values.students)
      if (result.success) {
        toast.success('Students registered successfully!', { position: toast.POSITION.TOP_CENTER })
        onSubmitSuccess()
      } else {
        const errorMsg = result.error ?? 'Failed to register students'
        setSubmitError(errorMsg)
        toast.error(errorMsg, { position: toast.POSITION.TOP_CENTER })
      }
    } catch (err) {
      const errorMsg = 'An unexpected error occurred'
      setSubmitError(errorMsg)
      toast.error(errorMsg, { position: toast.POSITION.TOP_CENTER })
    } finally {
      actions.setSubmitting(false)
    }
  }

  return (
    <Formik
      initialValues={{ students: initialStudents }}
      validationSchema={StudentSchema}
      onSubmit={handleSubmit}
      validateOnMount
    >
      {({ values, errors, touched, isSubmitting, isValid }: FormikProps<StudentFormValues>) => (
        <Form className='w-full'>
          <div className='mb-4 sm:mb-6'>
            <Text
              className='font-bold text-dopage-lightGreen text-base sm:text-lg md:text-xl text-center'
              bold
              size='LG'
              text='Almost done! Enter student information below.'
            />
            <div className='mt-2 text-center'>
              <Text
                className='text-gray-400 text-sm'
                size='SM'
                text={`Progress: ${sessionDetails.studentsSubmitted} of ${sessionDetails.quantity} students registered`}
              />
            </div>
          </div>

          <FieldArray name='students'>
            {() => (
              <div className='space-y-4'>
                {values.students.map((_, index) => {
                  const studentNumber = sessionDetails.studentsSubmitted + index + 1
                  const studentErrors = errors.students?.[index] as StudentData | undefined
                  const studentTouched = touched.students?.[index]

                  return (
                    <div key={index} className='p-3 sm:p-4 border border-gray-600 rounded-md' style={{ backgroundColor: '#1E201F' }}>
                      <label className='block text-xs sm:text-sm font-medium text-gray-300 mb-2'>
                        Student #{studentNumber}
                      </label>

                      {/* Email Field */}
                      <div className='mb-2'>
                        <Field name={`students.${index}.email`}>
                          {({ field }: { field: { name: string, value: string, onChange: (e: React.ChangeEvent<HTMLInputElement>) => void, onBlur: (e: React.FocusEvent<HTMLInputElement>) => void } }) => (
                            <Input
                              id={`students.${index}.email`}
                              data-testid={`input-student-email-${index}`}
                              className='w-full py-2 sm:py-3 px-3 sm:px-4 text-base leading-tight min-h-[44px]'
                              type='email'
                              placeholder='Email Address'
                              autoComplete='off'
                              {...field}
                            />
                          )}
                        </Field>
                        {studentErrors?.email != null && studentTouched?.email === true && (
                          <div className='text-[#ff674c] pt-1 text-xs' aria-live='polite'>{studentErrors.email}</div>
                        )}
                      </div>

                      {/* Name Fields Row */}
                      <div className='flex flex-col sm:flex-row sm:space-x-3 space-y-2 sm:space-y-0'>
                        <div className='w-full sm:w-1/2'>
                          <Field name={`students.${index}.firstName`}>
                            {({ field }: { field: { name: string, value: string, onChange: (e: React.ChangeEvent<HTMLInputElement>) => void, onBlur: (e: React.FocusEvent<HTMLInputElement>) => void } }) => (
                              <Input
                                id={`students.${index}.firstName`}
                                data-testid={`input-student-firstname-${index}`}
                                className='w-full py-2 sm:py-3 px-3 sm:px-4 text-base leading-tight min-h-[44px]'
                                type='text'
                                placeholder='First Name'
                                autoComplete='off'
                                {...field}
                              />
                            )}
                          </Field>
                          {studentErrors?.firstName != null && studentTouched?.firstName === true && (
                            <div className='text-[#ff674c] pt-1 text-xs' aria-live='polite'>{studentErrors.firstName}</div>
                          )}
                        </div>
                        <div className='w-full sm:w-1/2'>
                          <Field name={`students.${index}.lastName`}>
                            {({ field }: { field: { name: string, value: string, onChange: (e: React.ChangeEvent<HTMLInputElement>) => void, onBlur: (e: React.FocusEvent<HTMLInputElement>) => void } }) => (
                              <Input
                                id={`students.${index}.lastName`}
                                data-testid={`input-student-lastname-${index}`}
                                className='w-full py-2 sm:py-3 px-3 sm:px-4 text-base leading-tight min-h-[44px]'
                                type='text'
                                placeholder='Last Name'
                                autoComplete='off'
                                {...field}
                              />
                            )}
                          </Field>
                          {studentErrors?.lastName != null && studentTouched?.lastName === true && (
                            <div className='text-[#ff674c] pt-1 text-xs' aria-live='polite'>{studentErrors.lastName}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </FieldArray>

          {submitError != null && (
            <div className='mt-4 p-3 bg-red-100 border border-red-400 rounded text-center text-sm text-[#ff674c]' aria-live='polite'>
              {submitError}
            </div>
          )}

          <div className='mt-4 sm:mt-6'>
            <Button
              id='button-submit-students'
              data-testid='button-submit-students'
              className='diamond-btn min-h-[48px] sm:h-[52px] w-full font-bold flex justify-center items-center text-black disabled:opacity-50 disabled:cursor-not-allowed'
              type='submit'
              loading={isSubmitting}
              disabled={isSubmitting || !isValid}
            >
              <Text className='text-black text-base sm:text-lg' size='BASE' text='Submit Student Information' type='span' />
            </Button>
          </div>

          <div className='mt-3 text-center'>
            <Text
              className='text-gray-400 text-xs sm:text-sm'
              size='SM'
              text='📌 Bookmark this page to add students later if needed'
            />
          </div>
        </Form>
      )}
    </Formik>
  )
}

// --- Completion State Component ---
interface CompletionStateProps {
  existingStudents: StudentData[]
}

const CompletionState = ({ existingStudents }: CompletionStateProps): ReactElement => (
  <div className='text-center'>
    <div className='mb-4'>
      <Text
        className='font-bold text-dopage-lightGreen text-lg sm:text-xl md:text-2xl'
        bold
        size='LG'
        text='✅ All students registered!'
      />
    </div>
    <div className='mb-4'>
      <Text
        className='text-dopage-primary text-sm sm:text-base'
        size='SM'
        text='Sign-on instructions have been sent to:'
      />
    </div>
    <div className='space-y-2 mb-6'>
      {existingStudents.map((student, index) => (
        <div key={index} className='text-white text-sm'>
          • {student.email}
        </div>
      ))}
    </div>
    <Text
      className='text-gray-400 text-xs sm:text-sm'
      size='SM'
      text='Students should check their inboxes for a message from DopaGE.'
    />
  </div>
)

// --- Main Payment Success Component ---
function PaymentSuccessPage (): ReactElement {
  const [searchParams] = useSearchParams()
  const sessionId = useMemo(() => searchParams.get('session_id'), [searchParams])

  // Session details state
  const [sessionLookupStatus, setSessionLookupStatus] = useState<SessionLookupStatus>('idle')
  const [sessionDetails, setSessionDetails] = useState<SessionDetails | null>(null)
  const [sessionError, setSessionError] = useState<string | null>(null)

  // Coupon state (existing functionality)
  const [coupon, setCoupon] = useState<CouponInfo | null>(null)
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle')

  // Fetch session details
  const loadSessionDetails = useCallback(async (signal?: AbortSignal): Promise<void> => {
    if (sessionId == null || sessionId === '') return

    setSessionLookupStatus('loading')
    setSessionError(null)

    const result = await fetchSessionDetails(sessionId, signal)

    if (result.success && result.data != null) {
      setSessionDetails(result.data)
      setSessionLookupStatus('success')
    } else if (result.code === 'payment_pending') {
      setSessionLookupStatus('payment_pending')
      setSessionError('Payment is still being processed. Please wait a moment and refresh.')
    } else {
      setSessionLookupStatus('error')
      setSessionError(result.error ?? 'Failed to load session details')
    }
  }, [sessionId])

  useEffect(() => {
    const controller = new AbortController()

    if (sessionId != null && sessionId !== '') {
      logger.log(`Payment success page loaded for session ID: ${sessionId}`)
      void loadSessionDetails(controller.signal)
    } else {
      logger.error('Payment success page loaded without a session ID.')
      setSessionLookupStatus('error')
      setSessionError('No session ID provided')
    }

    return () => {
      controller.abort()
    }
  }, [sessionId, loadSessionDetails])

  // Fetch coupon info (existing functionality)
  useEffect(() => {
    let isMounted = true
    const controller = new AbortController()

    const fetchDiscount = async (): Promise<void> => {
      if (sessionId == null || sessionId === '') return
      try {
        const res = await fetch(WEBHOOK_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'CHECKOUT_SESSION_LOOKUP', sessionId }),
          signal: controller.signal
        })
        const data = await res.json()
        if (!isMounted) return
        if (data?.success === true && data.coupon?.code != null) {
            setCoupon({ code: data.coupon.code, discountPercentage: Number(data.coupon.discountPercentage) || 0 })
        }
      } catch (_err) {
        // Silently fail for coupon lookup
      }
    }

    void fetchDiscount()

    return () => {
      isMounted = false
      controller.abort()
    }
  }, [sessionId])

  const handleCopyCode = async (code: string): Promise<void> => {
    try {
      if (navigator?.clipboard?.writeText != null) {
        await navigator.clipboard.writeText(code)
      } else {
        const tempInput = document.createElement('textarea')
        tempInput.value = code
        document.body.appendChild(tempInput)
        tempInput.select()
        document.execCommand('copy')
        document.body.removeChild(tempInput)
      }
      setCopyState('copied')
      setTimeout(() => { setCopyState('idle') }, 1500)
    } catch (_err) {
      setCopyState('error')
      setTimeout(() => { setCopyState('idle') }, 1500)
    }
  }

  const handleStudentSubmitSuccess = (): void => {
    // Reload session details to get updated state
    void loadSessionDetails()
  }

  // Add custom styles
  React.useEffect(() => {
    const styleEl = document.createElement('style')
    styleEl.innerHTML = `
            .payment-success-container > div > div > div.diamond-corner > div.inner.bg-black {
                background-color: #1E201F !important;
            }
            .payment-success-container > div {
                background-color: #1E201F !important;
            }
            
            @media (max-width: 640px) {
                .payment-success-container,
                .payment-success-container > div,
                .payment-success-container > div > div,
                .payment-success-container > div > div > div {
                    max-width: 100% !important;
                    width: 100% !important;
                    min-width: 0 !important;
                }
            }
        `
    document.head.appendChild(styleEl)

    return () => {
      if (styleEl.parentNode === document.head) {
        document.head.removeChild(styleEl)
      }
    }
  }, [])

  // Inject global styles to force scrolling
  React.useEffect(() => {
    document.documentElement.style.overflow = 'auto'
    document.body.style.overflow = 'auto'
    document.body.style.height = 'auto'
    document.body.style.minHeight = '100vh'
    document.body.style.position = 'relative'

    return () => {
      document.documentElement.style.removeProperty('overflow')
      document.body.style.removeProperty('overflow')
      document.body.style.removeProperty('height')
      document.body.style.removeProperty('min-height')
      document.body.style.removeProperty('position')
    }
  }, [])

  // Determine what to show based on session state
  const isComplete = sessionDetails?.studentCollectionStatus === 'complete'
  // Show student collection form for ANY paid session with remaining slots
  // This covers both deferred flow (new) and immediate flow (old) where students need to be added
  const needsStudentCollection = sessionDetails != null &&
    sessionDetails.remainingSlots > 0 &&
    sessionDetails.paymentStatus === 'paid'

  return (
    <div style={{
      minHeight: '100vh',
      overflow: 'auto',
      position: 'relative',
      backgroundColor: '#101111'
    }}
    >
      {/* Logo */}
      <header className='flex justify-center pt-4 pb-1 sm:absolute sm:left-6 sm:top-6 sm:pt-0 sm:pb-0 md:left-[40px] md:top-[40px]' aria-hidden='true'>
        <img src={logo} className='w-[80px] sm:w-[120px] md:w-[138px]' alt='logo' />
      </header>

      <div
        className='pt-2 sm:pt-10 px-4 sm:px-6 md:px-8 lg:px-0 payment-success-container'
        style={{
          maxWidth: '680px',
          width: '100%',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        <ModalStripe tall>
          <Text
            className='font-bold text-center text-xl sm:text-2xl md:text-3xl text-dopage-darkGreen mb-2 sm:mb-4'
            bold
            size='LG'
            text='Success'
            type='paragraph'
          />

          <div className='w-full px-2 sm:px-4 md:px-8 flex flex-col items-center'>
            <Text
              className='font-bold text-dopage-lightGreen text-lg sm:text-xl md:text-2xl text-center leading-snug mb-4 sm:mb-6'
              bold
              size='LG'
              text='You just purchased something priceless: the power to save a life.'
              type='paragraph'
            />

            {/* Loading State */}
            {sessionLookupStatus === 'loading' && (
              <div className='text-center text-gray-400 text-sm py-8'>
                Loading session details...
              </div>
            )}

            {/* Error State */}
            {sessionLookupStatus === 'error' && (
              <div className='text-center py-4'>
                <div className='text-red-400 text-sm mb-4'>{sessionError}</div>
                <Button
                  onClick={() => { void loadSessionDetails() }}
                  className='diamond-btn min-h-[40px] px-6 font-bold flex justify-center items-center text-black'
                >
                  <Text className='text-black text-sm' text='Retry' />
                </Button>
              </div>
            )}

            {/* Payment Pending State */}
            {sessionLookupStatus === 'payment_pending' && (
              <div className='text-center py-4'>
                <div className='text-yellow-400 text-sm mb-4'>{sessionError}</div>
                <Button
                  onClick={() => { void loadSessionDetails() }}
                  className='diamond-btn min-h-[40px] px-6 font-bold flex justify-center items-center text-black'
                >
                  <Text className='text-black text-sm' text='Check Again' />
                </Button>
              </div>
            )}

            {/* Student Collection Form */}
            {sessionLookupStatus === 'success' && needsStudentCollection && sessionId != null && (
              <div className='w-full max-w-md'>
                <StudentCollectionForm
                  sessionId={sessionId}
                  sessionDetails={sessionDetails}
                  onSubmitSuccess={handleStudentSubmitSuccess}
                />
              </div>
            )}

            {/* Completion State */}
            {sessionLookupStatus === 'success' && isComplete && sessionDetails != null && (
              <CompletionState existingStudents={sessionDetails.existingStudents} />
            )}

            {/* Legacy flow - show original message only if NO remaining slots and not complete */}
            {sessionLookupStatus === 'success' && sessionDetails != null && !needsStudentCollection && !isComplete && (
              <div className='mt-4 sm:mt-6 text-center max-w-md'>
              <Text
                  className='text-dopage-primary text-sm sm:text-base md:text-lg leading-relaxed'
                  size='SM'
                text='Sign-on instructions have been sent directly to the student email(s) you provided. Students should check their inboxes for a message from DopaGE.'
              />
            </div>
                )}

            {/* Discount Info block */}
            {sessionId != null && coupon != null && (
              <div className='mt-4 sm:mt-6 w-full max-w-md'>
                  <div data-testid='discount-info' className='p-3 sm:p-4 border border-gray-700 rounded-md bg-[#1E201F] flex flex-col items-center justify-center text-center space-y-1.5 sm:space-y-2'>
                    <div className='text-white text-xs sm:text-sm font-semibold'>Share with friends!</div>
                    <div className='flex items-center gap-2'>
                      <div className='text-dopage-primary text-xs sm:text-sm'>
                        Code: <span className='font-mono'>{coupon.code}</span>
                      </div>
                      <button
                        type='button'
                        aria-label='Copy discount code'
                        data-testid='copy-code-button'
                      onClick={() => { void handleCopyCode(coupon.code) }}
                        className='text-gray-300 hover:text-white'
                      >
                        <svg width='18' height='18' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true'>
                        <path d='M16 1H4a2 2 0 0 0-2 2v12h2V3h12V1zm3 4H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm0 16H8V7h11v14z' />
                        </svg>
                      </button>
                    </div>
                    <div className='text-white text-xs sm:text-sm'>${((coupon.discountPercentage / 100) * 25).toFixed(2)} off each student</div>
                    <div aria-live='polite' className='text-xs h-4'>
                      {copyState === 'copied' ? 'Copied!' : copyState === 'error' ? 'Copy failed' : ''}
                    </div>
                  </div>
              </div>
            )}

            <div className='mt-6 sm:mt-8 w-full max-w-sm'>
              <ContactLinks id='link-contact-success' />
            </div>
          </div>
        </ModalStripe>
      </div>
    </div>
  )
}

export default PaymentSuccessPage

